import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatStoreTime, toApiDate } from '@/lib/store-time';
import {
  useDeleteBusinessAccount,
  useSaveBusinessAccount,
} from './business-accounts.api';
import { SecretField } from './secret-field';
import {
  BUSINESS_TYPES,
  ENTITY_TYPE,
  businessTypeLabel,
  type BusinessAccountInput,
} from './types';

/**
 * A payout account.
 *
 * Grouped by what each part is *for* rather than by the order the binding model
 * declares things: where the money goes, who the processor is being told about,
 * and how the split works. The original was one column of thirty-odd inputs.
 *
 * The two bank numbers are masked until revealed — see `SecretField`.
 */
export function AccountEditor({
  account,
  open,
  onOpenChange,
}: {
  account: { id: string | null; values: BusinessAccountInput } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<BusinessAccountInput | null>(
    account?.values ?? null
  );
  const [birthDate, setBirthDate] = useState('');

  const businessesQuery = useBusinesses();
  const businesses = businessesQuery.data;
  const save = useSaveBusinessAccount();
  const remove = useDeleteBusinessAccount();

  useEffect(() => {
    setDraft(account?.values ?? null);
    setBirthDate(
      account?.values.birthDate
        ? formatStoreTime(account.values.birthDate, true)
        : ''
    );
  }, [account]);

  if (!draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl" />
      </Sheet>
    );
  }

  const id = account?.id ?? null;
  const isEdit = !!id;
  const busy = save.isPending || remove.isPending;
  const isCompany = draft.accountHolderType === ENTITY_TYPE.company;

  const set = <K extends keyof BusinessAccountInput>(
    key: K,
    value: BusinessAccountInput[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const canSave =
    !!draft.businessId &&
    !!draft.accountHolderName.trim() &&
    !!draft.accountNumber.trim() &&
    !!draft.routingNumber.trim();

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('businessAccounts.saveFailed')
    );

  function submit() {
    save.mutate(
      {
        id,
        values: {
          ...draft!,
          birthDate: birthDate ? toApiDate(birthDate) : null,
          // Left empty on purpose so the server records the address it saw;
          // see the note in business-accounts.api.
          acceptanceIp: '',
        },
      },
      {
        onSuccess: () => {
          toast.success(
            isEdit
              ? t('businessAccounts.updated')
              : t('businessAccounts.created')
          );
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit
              ? t('businessAccounts.editAccount')
              : t('businessAccounts.newAccount')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('businessAccounts.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('businessAccounts.delete')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('businessAccounts.deleteConfirm', {
                      name: draft.businessName,
                    })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(id!, {
                        onSuccess: () => {
                          toast.success(t('businessAccounts.deleted'));
                          onOpenChange(false);
                        },
                        onError: failed,
                      })
                    }
                  >
                    {t('businessAccounts.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-6 p-4">
          {/* ------------------------------------------------ the store */}
          <Section title={t('businessAccounts.store')}>
            <Field label={t('businessAccounts.store')}>
              {/* The stored store's name comes from this list, so the trigger
                  would read blank until it arrives. */}
              {businessesQuery.isLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <Select
                  value={draft.businessId ?? ''}
                  disabled={busy || isEdit}
                  onValueChange={(value) => set('businessId', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t('businessAccounts.chooseStore')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(businesses ?? []).map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle
                label={t('businessAccounts.isActive')}
                hint={t('businessAccounts.isActiveHint')}
                checked={draft.isActive}
                disabled={busy}
                onChange={(value) => set('isActive', value)}
              />
              <Toggle
                label={t('businessAccounts.allowPayouts')}
                hint={t('businessAccounts.allowPayoutsHint')}
                checked={draft.allowPayouts}
                disabled={busy}
                onChange={(value) => set('allowPayouts', value)}
              />
            </div>
          </Section>

          {/* ---------------------------------------------- bank details */}
          <Section title={t('businessAccounts.bank')}>
            <Field
              label={t('businessAccounts.accountHolderName')}
              htmlFor="accountHolderName"
            >
              <Input
                id="accountHolderName"
                value={draft.accountHolderName}
                disabled={busy}
                onChange={(event) =>
                  set('accountHolderName', event.target.value)
                }
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <SecretField
                label={t('businessAccounts.accountNumber')}
                htmlFor="accountNumber"
                value={draft.accountNumber}
                disabled={busy}
                onChange={(value) => set('accountNumber', value)}
              />
              <SecretField
                label={t('businessAccounts.routingNumber')}
                htmlFor="routingNumber"
                value={draft.routingNumber}
                disabled={busy}
                onChange={(value) => set('routingNumber', value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('businessAccounts.minimumPayout')}
                htmlFor="minimumPayoutAmount"
                hint={t('businessAccounts.minimumPayoutHint')}
              >
                <Input
                  id="minimumPayoutAmount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.minimumPayoutAmount}
                  disabled={busy}
                  onChange={(event) =>
                    set('minimumPayoutAmount', Number(event.target.value))
                  }
                />
              </Field>
              <Field
                label={t('businessAccounts.processorAccountId')}
                htmlFor="accountId"
                hint={t('businessAccounts.processorAccountIdHint')}
              >
                <Input
                  id="accountId"
                  value={draft.accountId}
                  disabled={busy}
                  className="font-mono text-xs"
                  onChange={(event) => set('accountId', event.target.value)}
                />
              </Field>
            </div>
            <Field
              label={t('businessAccounts.payPalClientId')}
              htmlFor="payPalClientId"
            >
              <Input
                id="payPalClientId"
                value={draft.payPalClientId}
                disabled={busy}
                className="font-mono text-xs"
                onChange={(event) => set('payPalClientId', event.target.value)}
              />
            </Field>
          </Section>

          {/* ------------------------------------------------- the entity */}
          <Section title={t('businessAccounts.entity')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('businessAccounts.holderType')}>
                <Select
                  value={String(draft.accountHolderType)}
                  disabled={busy}
                  onValueChange={(value) =>
                    set('accountHolderType', Number(value))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(ENTITY_TYPE.company)}>
                      {t('businessAccounts.company')}
                    </SelectItem>
                    <SelectItem value={String(ENTITY_TYPE.individual)}>
                      {t('businessAccounts.individual')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('businessAccounts.businessType')}>
                <Select
                  value={String(draft.businessType)}
                  disabled={busy}
                  onValueChange={(value) => set('businessType', Number(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((_, index) => (
                      <SelectItem key={index} value={String(index)}>
                        {businessTypeLabel(index)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {isCompany ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label={t('businessAccounts.legalName')}
                  htmlFor="businessName"
                >
                  <Input
                    id="businessName"
                    value={draft.businessName}
                    disabled={busy}
                    onChange={(event) =>
                      set('businessName', event.target.value)
                    }
                  />
                </Field>
                <Field label={t('businessAccounts.ein')} htmlFor="EIN">
                  <Input
                    id="EIN"
                    value={draft.EIN}
                    disabled={busy}
                    className="font-mono text-sm"
                    onChange={(event) => set('EIN', event.target.value)}
                  />
                </Field>
              </div>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label={t('businessAccounts.firstName')}
                    htmlFor="firstName"
                  >
                    <Input
                      id="firstName"
                      value={draft.firstName}
                      disabled={busy}
                      onChange={(event) =>
                        set('firstName', event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t('businessAccounts.lastName')}
                    htmlFor="lastName"
                  >
                    <Input
                      id="lastName"
                      value={draft.lastName}
                      disabled={busy}
                      onChange={(event) => set('lastName', event.target.value)}
                    />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label={t('businessAccounts.birthDate')}
                    htmlFor="birthDate"
                  >
                    <Input
                      id="birthDate"
                      type="date"
                      value={birthDate}
                      disabled={busy}
                      onChange={(event) => setBirthDate(event.target.value)}
                    />
                  </Field>
                  <SecretField
                    label={t('businessAccounts.last4Ssn')}
                    htmlFor="last4Ssn"
                    hint={t('businessAccounts.last4SsnHint')}
                    value={draft.last4Ssn}
                    disabled={busy}
                    onChange={(value) => set('last4Ssn', value)}
                  />
                </div>
              </>
            )}

            <Field
              label={t('businessAccounts.accountName')}
              htmlFor="accountName"
              hint={t('businessAccounts.accountNameHint')}
            >
              <Input
                id="accountName"
                value={draft.accountName}
                disabled={busy}
                onChange={(event) => set('accountName', event.target.value)}
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('businessAccounts.contactEmail')}
                htmlFor="contactEmail"
              >
                <Input
                  id="contactEmail"
                  type="email"
                  value={draft.contactEmail}
                  disabled={busy}
                  onChange={(event) => set('contactEmail', event.target.value)}
                />
              </Field>
              <Field
                label={t('businessAccounts.contactPhone')}
                htmlFor="contactPhone"
              >
                <Input
                  id="contactPhone"
                  type="tel"
                  value={draft.contactPhone}
                  disabled={busy}
                  onChange={(event) => set('contactPhone', event.target.value)}
                />
              </Field>
            </div>
          </Section>

          {/* ---------------------------------------------------- address */}
          <Section title={t('businessAccounts.address')}>
            <Field label={t('businessAccounts.address1')} htmlFor="address1">
              <Input
                id="address1"
                value={draft.address1}
                disabled={busy}
                onChange={(event) => set('address1', event.target.value)}
              />
            </Field>
            <Field label={t('businessAccounts.address2')} htmlFor="address2">
              <Input
                id="address2"
                value={draft.address2}
                disabled={busy}
                onChange={(event) => set('address2', event.target.value)}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label={t('businessAccounts.city')} htmlFor="city">
                <Input
                  id="city"
                  value={draft.city}
                  disabled={busy}
                  onChange={(event) => set('city', event.target.value)}
                />
              </Field>
              <Field label={t('businessAccounts.state')} htmlFor="state">
                <Input
                  id="state"
                  value={draft.state}
                  disabled={busy}
                  onChange={(event) => set('state', event.target.value)}
                />
              </Field>
              <Field
                label={t('businessAccounts.postalCode')}
                htmlFor="postalCode"
              >
                <Input
                  id="postalCode"
                  value={draft.postalCode}
                  disabled={busy}
                  onChange={(event) => set('postalCode', event.target.value)}
                />
              </Field>
              <Field label={t('businessAccounts.country')} htmlFor="country">
                <Input
                  id="country"
                  value={draft.country}
                  disabled={busy}
                  onChange={(event) => set('country', event.target.value)}
                />
              </Field>
            </div>
          </Section>

          {/* -------------------------------------------------- the split */}
          <Section title={t('businessAccounts.split')}>
            <p className="text-muted-foreground text-xs">
              {t('businessAccounts.splitHint')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['subtotalPercentage', t('businessAccounts.subtotalPct')],
                  ['taxesPercentage', t('businessAccounts.taxesPct')],
                  ['serviceFeePercentage', t('businessAccounts.serviceFeePct')],
                  ['processorPercentage', t('businessAccounts.processorPct')],
                ] as const
              ).map(([key, label]) => (
                <Field key={key} label={label} htmlFor={key}>
                  <Input
                    id={key}
                    type="number"
                    step="0.01"
                    value={draft[key]}
                    disabled={busy}
                    onChange={(event) =>
                      set(key, Number(event.target.value))
                    }
                  />
                </Field>
              ))}
            </div>
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('businessAccounts.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
